import type { Resolver } from 'react-hook-form';

import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FullCV,
  Input,
} from '@cibli/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import React from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { getCurrentMonthFirstDayISO, getDateForSort } from '@/utils/dateFormatters';

import { useValidateOnMount } from '../../hooks/useValidateOnMount';
import { useVocalFormUpdate } from '../../hooks/useVocalFormUpdate';
import { createEducationSchema } from '../../schemas/education.schema';

interface EducationsFormProps {
  onSubmit: (data: FullCV) => void;
  initialData?: FullCV;
  onCancel: () => void;
}

const EducationsForm: React.FC<EducationsFormProps> = ({ onSubmit, initialData, onCancel }) => {
  const { t } = useTranslation();

  const educationSchema = createEducationSchema(t);
  const educationsSchema = z.object({
    education: z.array(educationSchema).optional(),
  });

  type EducationsFormValues = z.infer<typeof educationsSchema>;

  const defaultEducation = initialData?.education?.length
    ? initialData.education.map(e => ({
        school: e.school ?? '',
        name: e.name ?? '',
        degree: e.degree ?? '',
        location: e.location ?? '',
        degreeStartedAt: e.degreeStartedAt ?? '',
        degreeObtainedAt: e.degreeObtainedAt ?? '',
        acquiredSkills: e.acquiredSkills ?? [],
      }))
    : [
        {
          school: '',
          name: '',
          degree: '',
          location: '',
          degreeStartedAt: '',
          degreeObtainedAt: '',
          acquiredSkills: [] as string[],
        },
      ];

  const form = useForm<EducationsFormValues>({
    resolver: zodResolver(educationsSchema) as Resolver<EducationsFormValues>,
    defaultValues: {
      education: defaultEducation,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'education',
  });

  const handleSubmit = (data: EducationsFormValues) => {
    const sorted = {
      ...data,
      education: [...(data.education ?? [])].sort((a, b) =>
        getDateForSort(b.degreeObtainedAt ?? null, b.degreeStartedAt ?? null).localeCompare(
          getDateForSort(a.degreeObtainedAt ?? null, a.degreeStartedAt ?? null),
        ),
      ),
    };
    onSubmit(sorted as FullCV);
  };

  useValidateOnMount(form);
  useVocalFormUpdate(form, 'education');

  /**
   * Stored date ('2021' | '2021-05' | '2021-05-01') → text input display
   * ('2021' when only the year is known, else 'MM/YYYY').
   * The month is never blocking: a year alone is a valid value.
   */
  const formatDateToMonth = (date: string | undefined) => {
    if (!date || date === 'CURRENT') return '';
    if (/^\d{4}$/.test(date)) return date;
    const match = /^(\d{4})-(\d{2})/.exec(date);
    if (match) return `${match[2]}/${match[1]}`;
    // Pass through anything else (text being typed) so input is never wiped
    // mid-keystroke; validation happens on blur/save instead.
    return date;
  };

  /**
   * Text input value ('2021' | 'MM/YYYY' | 'YYYY-MM') → stored format.
   */
  const formatMonthToDate = (monthStr: string) => {
    const value = monthStr.trim();
    if (!value) return '';
    if (/^\d{4}$/.test(value)) return value;
    const slash = /^(\d{2})\/(\d{4})$/.exec(value);
    if (slash) return `${slash[2]}-${slash[1]}-01`;
    const iso = /^(\d{4})-(\d{2})$/.exec(value);
    if (iso) return `${iso[1]}-${iso[2]}-01`;
    return value;
  };

  return (
    <div className="container mx-auto p-4 max-w-2xl">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-2 w-full items-center mb-4">
            <h3 className="text-lg text-primary">{t('Forms.title')}</h3>
            <h1 className="text-2xl font-bold">{t('Forms.Education.title')}</h1>
          </div>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-6 items-stretch pb-20"
          >
            {fields.map((field, index) => (
              <div key={field.id} className="flex flex-row gap-4">
                <div className="flex items-center justify-center w-6 h-6 bg-primary text-white rounded-full mt-10">
                  {index + 1}
                </div>
                <div className="flex-1 p-2 relative bg-transparent">
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name={`education.${index}.school`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>{t('Forms.Education.school')}</FormLabel>
                          <FormControl>
                            <Input
                              className="h-12"
                              placeholder={t('Forms.Education.schoolPlaceholder')}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`education.${index}.degree`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>{t('Forms.Education.degreeType')}</FormLabel>
                          <FormControl>
                            <Input
                              className="h-12"
                              placeholder={t('Forms.Education.degreePlaceholder')}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`education.${index}.name`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>{t('Forms.Education.formationName')}</FormLabel>
                          <FormControl>
                            <Input
                              className="h-12"
                              placeholder={t('Forms.Education.formationNamePlaceholder')}
                              {...field}
                              value={field.value ?? ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`education.${index}.location`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('Forms.PastExperience.location')}</FormLabel>
                          <FormControl>
                            <Input
                              className="h-12"
                              placeholder={t('Forms.PastExperience.locationPlaceholder')}
                              {...field}
                              value={field.value ?? ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`education.${index}.acquiredSkills`}
                      render={({ field }) => {
                        const skills = (field.value ?? []) as string[];
                        return (
                          <FormItem>
                            <FormLabel>{t('Forms.Education.acquiredSkills')}</FormLabel>
                            <div className="space-y-2">
                              {skills.map((_, skillIndex) => (
                                <div key={skillIndex} className="flex gap-2 items-center">
                                  <FormControl>
                                    <Input
                                      className="h-10 flex-1"
                                      placeholder={t('Forms.Education.competencePlaceholder')}
                                      value={skills[skillIndex] ?? ''}
                                      onChange={e => {
                                        const next = [...skills];
                                        next[skillIndex] = e.target.value;
                                        field.onChange(next);
                                      }}
                                    />
                                  </FormControl>
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="icon"
                                    className="h-10 w-10 shrink-0 text-destructive hover:text-destructive/90"
                                    onClick={() => {
                                      const next = skills.filter((_, i) => i !== skillIndex);
                                      field.onChange(next);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                className="gap-1"
                                onClick={() => field.onChange([...skills, ''])}
                              >
                                <Plus className="h-4 w-4" />
                                {t('Forms.Education.addCompetence')}
                              </Button>
                            </div>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name={`education.${index}.degreeStartedAt`}
                        render={({ field: { onChange, value, ...field } }) => (
                          <FormItem>
                            <FormLabel>{t('Forms.Education.degreeStartedAt')}</FormLabel>
                            <FormControl>
                              <Input
                                className="h-12"
                                type="text"
                                placeholder={t('Forms.Education.degreeObtainedAtPlaceholder')}
                                {...field}
                                value={formatDateToMonth((value as string | null) ?? '')}
                                onChange={e => {
                                  const v = e.target.value;
                                  onChange(v ? formatMonthToDate(v) : null);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`education.${index}.degreeObtainedAt`}
                        render={({ field: { onChange, value, ...field } }) => {
                          const dateVal = (value as string | null) ?? '';
                          const isCurrentMonth =
                            dateVal !== '' &&
                            formatDateToMonth(dateVal) ===
                              formatDateToMonth(getCurrentMonthFirstDayISO());
                          const isStillInProgress = dateVal === 'CURRENT' || isCurrentMonth;

                          return (
                            <FormItem>
                              <div className="flex items-center justify-between">
                                <FormLabel>{t('Forms.Education.degreeObtainedAt')}</FormLabel>
                                <div className="flex items-center gap-1.5">
                                  <Checkbox
                                    id={`edu-${index}-still-in-progress`}
                                    checked={isStillInProgress}
                                    onCheckedChange={checked => {
                                      if (checked) {
                                        onChange(getCurrentMonthFirstDayISO());
                                      } else {
                                        onChange(null);
                                      }
                                    }}
                                    className="align-middle size-5"
                                  />
                                  <FormLabel
                                    htmlFor={`edu-${index}-still-in-progress`}
                                    className="text-sm font-medium cursor-pointer leading-none text-foreground"
                                  >
                                    {t('Forms.Education.stillInProgress')}
                                  </FormLabel>
                                </div>
                              </div>
                              {isStillInProgress ? (
                                <div className="flex h-12 items-center px-3 rounded-md border border-input bg-muted/40 text-sm text-foreground">
                                  {t('Forms.Education.stillInProgress')}
                                </div>
                              ) : (
                                <FormControl>
                                  <Input
                                    className={`h-12 ${
                                      form.formState.errors.education?.[index]?.degreeObtainedAt
                                        ? 'border-destructive focus-visible:ring-destructive'
                                        : ''
                                    }`}
                                    type="text"
                                    placeholder={t('Forms.Education.degreeObtainedAtPlaceholder')}
                                    {...field}
                                    value={formatDateToMonth(dateVal)}
                                    onChange={e => {
                                      const v = e.target.value;
                                      onChange(v ? formatMonthToDate(v) : null);
                                    }}
                                  />
                                </FormControl>
                              )}
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />
                    </div>
                    <div className="h-[1px] w-full bg-gray-200 mt-7" />
                  </div>
                </div>
                <div className="mt-7">
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    onClick={() => remove(index)}
                    className="shrink-0 text-destructive hover:text-destructive/90 h-12 w-12"
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            ))}

            <div className="flex justify-center">
              <Button
                type="button"
                onClick={() =>
                  append({
                    school: '',
                    name: '',
                    degree: '',
                    location: '',
                    degreeStartedAt: '',
                    degreeObtainedAt: '',
                    acquiredSkills: [],
                  })
                }
                className="bg-primary text-white hover:bg-primary/90"
              >
                {t('Forms.Education.addEducation')}
              </Button>
            </div>

            <div className="flex justify-between gap-4 mt-6">
              <Button type="button" size="lg" variant="secondary" onClick={onCancel}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" size="lg" className="bg-primary text-white hover:bg-primary/90">
                {t('common.save')}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default EducationsForm;
